<script setup lang="ts" generic="T extends RelatiegegevensTab">
import VSpinner from '@shared/component/VSpinner/VSpinner.vue';
import { sleep } from '@tools/helpers';
import { v4 as uuidv4 } from 'uuid';
import { onActivated, onDeactivated, onMounted, type Ref, ref, watch } from 'vue';

import { env } from '@configuration/env';
import { logger } from '@logging/logging';
import { type RelatiegegevensTab, relatiegegevensTabs } from './relatiegegevensUtils';

const props = defineProps<{
  relatienummer: number;
  tab?: T;
  overrideSrc?: string;
}>();

const iframeName = uuidv4();
const iframeRef: Ref<HTMLIFrameElement | undefined> = ref();
const initialRenderCompleted = ref(false);
const iframeMutationObserver: Ref<MutationObserver | undefined> = ref(undefined);
const isLoading = ref(true);
const showLoadingSpinner = ref(false);

defineExpose({
  click: (selector: string) => {
    // we gebruiken een interval omdat we niet weten wanneer de content in het iFrame geladen is
    let count = 0;
    const interval = setInterval(() => {
      count++;
      if (count > 50) clearInterval(interval);
      const target = iframeRef.value!.contentDocument!.body.querySelector(
        selector,
      ) as HTMLAnchorElement | null;
      if (target) {
        clearInterval(interval);
        target.click();
      }
    }, 500);
  },
});

const resizeIframe = (iframe: HTMLIFrameElement) => {
  const iframeDocumentHeight = iframe.contentWindow!.document.documentElement.scrollHeight;

  const popupPaddingHeight = 64; // 4rem = 64px
  const sweetAlertPopupHeight =
    iframe.contentWindow?.document.body
      .querySelector('.sweet-alert.showSweetAlert')
      ?.getBoundingClientRect()?.height ?? 0;
  const iframeHeightForPopup =
    sweetAlertPopupHeight > 0 ? sweetAlertPopupHeight + popupPaddingHeight : 0;

  iframe.style.minHeight = `${Math.max(iframeDocumentHeight, iframeHeightForPopup)}px`;
};

const onIframeLoaded = async (iframe: HTMLIFrameElement) => {
  if (props.tab) {
    const tabHref = relatiegegevensTabs[props.tab];

    const tabButton = iframe.contentDocument!.body.querySelector(
      `ul.nav-tabs a.nav-link[href="${tabHref}"]`,
    ) as HTMLAnchorElement | null;

    if (tabButton) {
      await sleep(100);
      tabButton.click();
    } else if (env.NODE_ENV !== 'test') {
      logger.error(`kon navigatiebutton voor tab ${props.tab} niet vinden`);
    }
  }

  if (iframeMutationObserver.value === undefined) {
    iframeMutationObserver.value = new MutationObserver(() => resizeIframe(iframe));
  }

  iframeMutationObserver.value.observe(iframe.contentDocument!.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });
  iframe.classList.remove('hidden');
  isLoading.value = false;
};

const init = (iframe: HTMLIFrameElement) => {
  iframe.onload = () => onIframeLoaded(iframe);
  iframe.src = props.overrideSrc ?? `/net/relation/Detail?id=${props.relatienummer}&embedMode=1`;
};

onMounted(() => {
  if (
    initialRenderCompleted.value === false &&
    iframeRef.value !== undefined &&
    isLoading.value === true
  ) {
    initialRenderCompleted.value = true;
    init(iframeRef.value!);
  }
});

onActivated(() => {
  isLoading.value = true;
  init(iframeRef.value!);
});

onDeactivated(() => {
  if (iframeMutationObserver.value !== undefined) {
    iframeMutationObserver.value?.disconnect();
  }
  iframeRef.value!.classList.add('hidden');
});

watch(
  isLoading,
  (newValue) => {
    if (newValue) {
      setTimeout(() => {
        if (isLoading.value === true) {
          showLoadingSpinner.value = true;
        }
      }, 1000);
    } else {
      showLoadingSpinner.value = false;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="w-full">
    <div class="flex justify-center text-3xl" v-if="isLoading && showLoadingSpinner">
      <VSpinner />
    </div>
    <iframe ref="iframeRef" class="hidden w-full" title="tabblad" :name="iframeName"></iframe>
  </div>
</template>
